<template>
  <div>
    <v-row no-gutters class="mx-2 mt-2" justify="space-between">
      <v-col cols="3" class="d-flex align-center justify-center">
        <v-btn class="third white--text" @click="handleDialogStatus"> {{ year }}</v-btn>
        <v-dialog v-model="dialog" class="pa-0" width="290">
          <v-card class="d-flex flex-column align-end pa-0">
            <v-card-text class="d-flex justify-center pa-0">
              <v-date-picker
                ref="datePickerRef"
                color="third"
                :active-picker.sync="activePicker"
                @click:year="pickDate"
              />
            </v-card-text>
          </v-card>
        </v-dialog>
      </v-col>
      <v-col cols="3">
        <v-btn class="third white--text" @click="openCreateDialog"> Nouvelle période </v-btn>
      </v-col>
    </v-row>
    <v-row no-gutters class="px-2">
      <v-data-table
        :headers="headers"
        :items="holidays"
        :loading="loading"
      >
        <template #progress>
          <v-progress-linear
            indeterminate
            color="third"
          />
        </template>
        <template #item.date="{ item }">
          {{ dateToShow(item) }}
        </template>
        <template #item.actions="{ item }">
          <v-btn icon @click="showEditModal(item)">
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
          <v-btn icon @click="showDeleteConfirmationModal(item)">
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-row>
    <!-- create dialog -->
    <v-dialog v-model="createDialog">
      <v-card>
        <v-card-title class="third white--text"> {{ modalLabel }}</v-card-title>
        <v-card-text>
          <v-form>
            <TextField
              v-model="newPeriod.name"
              :single-line="true"
              subtitle="Nom de la période"
            />
            <RadioGroup
              :value="typeOfVacance"
              :row="true"
              :options="radioOptions"
              class="ml-4"
              @input="updateRadioValue"
            />
            <DatePickerRange v-if="isVacancePeriod" v-model="newPeriod.dates" title="Période de vacance" icon="mdi-clock" />
            <DatePicker v-else v-model="newPeriod.date" title="Jour de férie" icon="mdi-clock" />
          </v-form>
        </v-card-text>
        <v-card-actions class="flex-grow-1 justify-end">
          <v-btn class="third--text" text @click="handleCreateDialogStatus">Annuler</v-btn>
          <v-btn class="third--text" :loading="loading" text :disabled="!isValidCreatePeriod" @click="handleCreateEditHoliday">{{ btnLabel }} </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <ConfirmationModal
      btn-label="effacer"
      btn-icon="mdi-delete"
      style="z-index: 100000;"
      title="Férie"
      :activator.sync="showConfirmationModal"
      :close-modal="closeConfirmationModal"
      :action="deleteHolidayClick"
      :loading="btnLoading"
    >
      <template #body>
        <h3 class="black--text">
          Êtes-vous sûr de vouloir supprimer le jour férie?
        </h3>
      </template>
    </ConfirmationModal>
  </div>
</template>

<script>
import { ref, onBeforeMount, computed, watch } from '@vue/composition-api';
import { endOfDay, format, fromUnixTime, getUnixTime, startOfDay } from 'date-fns';

import { mandatoryRule, sendNotification } from '@/helpers/utils';
import { getHolidaysByPeriod, createHoliday, updateHoliday, deleteHoliday } from '@/helpers/request';
import { periodType } from '@/helpers/variables';

import DatePicker from '@/components/common/Form/DatePicker.vue';
import DatePickerRange from '@/components/common/Form/DatePickerRange.vue';
import TextField from '@/components/common/Form/TextField.vue';
import RadioGroup from '../components/common/RadioGroup.vue';

import ConfirmationModal from '@/components/common/ConfirmationModal.vue';

import store from '@/store';

export default {
  components: { DatePicker, DatePickerRange, TextField, ConfirmationModal, RadioGroup },
  setup () {
    const selectedDate = computed(() => store.getters.selectedDate);

    const holidays = ref([]);
    const year = ref(format(new Date(selectedDate.value), 'yyyy'));
    const dialog = ref(false);
    const createDialog = ref(false);
    const loading = ref(false);
    const showConfirmationModal = ref(false);
    const selectedHoliday = ref(null);
    const isEdit = ref(false);
    const btnLoading = ref(false);
    const newPeriod = ref({ name: null, date: null, dates: [] });
    const typeOfVacance = ref('holiday');

    const activePicker = ref(null);
    const datePickerRef = ref(null);

    const radioOptions = ref([
      { label: 'Jour Ferié', value: 'holiday' },
      { label: 'Vacances', value: 'vacation' }
    ]);

    const headers = ref([
      { text: 'Type', value: 'type', align: 'center', sortable: false },
      { text: 'Jour / Période', value: 'date', align: 'center', sortable: false },
      { text: 'Nom', value: 'name', align: 'center', sortable: false },
      { text: '', value: 'actions', align: 'center', sortable: false }
    ]);

    const isVacancePeriod = computed(() => typeOfVacance.value === 'vacation');

    const isValidCreatePeriod = computed(() => newPeriod.value.name && ((isVacancePeriod.value && newPeriod.value.dates.length > 1) || (!isVacancePeriod.value && newPeriod.value.date)));

    const modalLabel = computed(() => (!isEdit.value ? 'Nouvelle période de Vacance' : 'Modifier les jours fériés'));
    const btnLabel = computed(() => (!isEdit.value ? 'Créer pour ajouter la période' : 'Modifier'));
    const currentWorkspace = computed(() => store.state.workspace);

    const handleDialogStatus = () => {
      dialog.value = !dialog.value;
    };

    const closeConfirmationModal = () => {
      showConfirmationModal.value = false;
      selectedHoliday.value = null;
    };

    const handleCreateDialogStatus = () => {
      createDialog.value = !createDialog.value;
    };

    const periodTypeLabel = label => (label === periodType.VACATION ? 'Vacances' : 'Jours fériés');

    const prepareHolidays = data => data.map(holiday => ({
      date: holiday.date,
      name: holiday.name,
      type: periodTypeLabel(holiday.type),
      start: holiday.startTimestamp,
      end: holiday.endTimestamp,
      rawType: holiday.type
    }));

    const getHolidays = async () => {
      dialog.value = false;
      loading.value = true;

      const start = getUnixTime(startOfDay(new Date(`${year.value}-01-01`)));
      const end = getUnixTime(endOfDay(new Date(`${year.value}-12-31`)));

      const response = await getHolidaysByPeriod(start, end, currentWorkspace.value.code);

      if (response && response.success) {
        holidays.value = prepareHolidays(response.data);
      }

      loading.value = false;
    };

    const pickDate = date => {
      year.value = parseInt(date);
      activePicker.value = null;
      getHolidays();
    };

    /* Workarround for year picker in vuetify 2.
      ActivePicker cycle needed: null -> (Month, after selecting year for the first time) -> Date -> Year
    */
    watch(activePicker, val => {
      if (val === 'MONTH') {
        activePicker.value = 'DATE';
        return;
      }

      if (val === 'DATE') {
        activePicker.value = 'YEAR';
        datePickerRef.value.yearClick(year.value);
      }
    });

    const formattedHoliday = (type, start, end) => ({
      name: newPeriod.value.name,
      date: format(fromUnixTime(start), 'yyyy-MM-dd'),
      workspace: currentWorkspace.value.code,
      timestampDate: start,
      startTimestamp: getUnixTime(startOfDay(fromUnixTime(start))),
      endTimestamp: getUnixTime(endOfDay(fromUnixTime(end))),
      type: type
    });

    const prepareHoliday = () => (isVacancePeriod.value ? formattedHoliday(periodType.VACATION, newPeriod.value.dates[0], newPeriod.value.dates[1]) : formattedHoliday(periodType.HOLIDAY, newPeriod.value.date, newPeriod.value.date));

    const editHoliday = async () => {
      loading.value = true;
      const body = prepareHoliday();

      // call API TO EDIT
      const response = await updateHoliday({ ...body, oldDate: selectedHoliday.value.date });

      if (response.success) {
        const index = holidays.value.findIndex(h => h.date === selectedHoliday.value.date);

        if (index !== -1) {
          holidays.value.splice(index, 1);
          holidays.value.push({
            date: response.data.date,
            name: response.data.name,
            type: periodTypeLabel(response.data.type),
            rawType: response.data.type,
            start: response.data.startTimestamp,
            end: response.data.endTimestamp
          });
        }
      }

      loading.value = false;
      selectedHoliday.value = null;
      isEdit.value = false;
      handleCreateDialogStatus();
      sendNotification(response.success ? 'success' : 'error', response.message);
    };

    const createNewHoliday = async () => {
      loading.value = true;
      // make request
      const body = prepareHoliday();

      const response = await createHoliday(body);

      if (response.success) {
        holidays.value.push({
          date: response.data.date,
          name: response.data.name,
          type: periodTypeLabel(response.data.type),
          rawType: response.data.type,
          start: response.data.startTimestamp,
          end: response.data.endTimestamp
        });
      }

      loading.value = false;
      handleCreateDialogStatus();
      sendNotification(response.success ? 'success' : 'error', response.message);
    };

    const handleCreateEditHoliday = () => {
      if (isEdit.value) {
        return editHoliday();
      }

      return createNewHoliday();
    };

    const updateRadioValue = value => {
      typeOfVacance.value = value;
    };

    const showEditModal = async holiday => {
      newPeriod.value = {
        ...holiday,
        date: getUnixTime(new Date(holiday.date)),
        dates: [holiday.start, holiday.end]
      };
      updateRadioValue(holiday.rawType.toLowerCase());
      isEdit.value = true;
      selectedHoliday.value = holiday;
      handleCreateDialogStatus();
    };

    const showDeleteConfirmationModal = item => {
      selectedHoliday.value = item;
      showConfirmationModal.value = true;
    };

    const deleteHolidayClick = async () => {
      btnLoading.value = true;
      const response = await deleteHoliday({ date: selectedHoliday.value.date, workspace: currentWorkspace.value.code });
      if (response.success) {
        const index = holidays.value.findIndex(h => h.date === selectedHoliday.value.date);

        if (index !== -1) {
          holidays.value.splice(index, 1);
        }
      }

      btnLoading.value = false;
      showConfirmationModal.value = false;
      sendNotification(response.success ? 'success' : 'error', response.message);
    };

    const openCreateDialog = () => {
      createDialog.value = true;
      isEdit.value = false;
      typeOfVacance.value = 'holiday';
      newPeriod.value = { name: null, date: null, dates: [] };
    };

    const dateToShow = item => {
      switch (item.rawType) {
        case 'VACATION': {
          return `Du ${item.date} à ${format(new Date(fromUnixTime(item.end)), 'yyyy-MM-dd')}`;
        }
        case 'HOLIDAY': {
          return item.date;
        }
        default:
          return item.date;
      }
    };

    onBeforeMount(async () => {
      year.value = format(new Date(selectedDate.value), 'yyyy');

      await getHolidays();
    });

    return {
      dateToShow,
      updateRadioValue,
      radioOptions,
      isValidCreatePeriod,
      startOfDay,
      getUnixTime,
      isVacancePeriod,
      typeOfVacance,
      btnLoading,
      showEditModal,
      btnLabel,
      modalLabel,
      handleCreateEditHoliday,
      showDeleteConfirmationModal,
      closeConfirmationModal,
      showConfirmationModal,
      editHoliday,
      deleteHolidayClick,
      holidays,
      year,
      dialog,
      handleDialogStatus,
      pickDate,
      headers,
      getHolidays,
      createNewHoliday,
      createDialog,
      handleCreateDialogStatus,
      openCreateDialog,
      mandatoryRule,
      newPeriod,
      loading,
      activePicker,
      datePickerRef
    };
  }
};

</script>
